import { Tooltip } from "antd";

import {
  ICustomFieldColumn,
  IPatientListCustomFieldValue,
} from "../../../../../../@types/CustomField";
import { Tag } from "../../../../../../components/Tag";
// Shared line typography so every tooltip in the portal reads the same.
import { TooltipLine } from "../../../../../../components/TooltipLine";
import { formatCustomColumnValue } from "../../../../../../utils/customFields";
import { getLongFormattedDate } from "../../../../../../utils/dateFormatter";
import { CustomFieldChips, CustomFieldValue } from "./CustomFieldCell.styles";

interface ICustomFieldCellProps {
  column: ICustomFieldColumn;
  values: IPatientListCustomFieldValue[] | undefined;
}

// EN-5067: a dynamic custom-field column cell. Multiselect → chips; everything else → the formatted
// scalar string. Hovering shows the value with the date it was last updated underneath (same
// tooltip treatment as the RAG measurement cells). Renders "-" (no tooltip) when there's no value.
export function CustomFieldCell({ column, values }: ICustomFieldCellProps) {
  const match = values?.find(
    (value) => value.customFieldDefinitionId === column.customFieldDefinitionId
  );

  const isMultiselect = column.type === "Multiselect";
  const selectedOptions = match?.selectedOptions ?? [];
  const displayValue = isMultiselect
    ? selectedOptions.join(", ")
    : formatCustomColumnValue(column.type, match);

  if (!displayValue || displayValue === "-") {
    return <>-</>;
  }

  const tooltipContent = (
    <>
      <TooltipLine emphasis>{displayValue}</TooltipLine>
      {/* The API only sends lastUpdated once the field has been answered. */}
      {match?.lastUpdated && (
        <TooltipLine>{getLongFormattedDate(match.lastUpdated)}</TooltipLine>
      )}
    </>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow={false}
      color="#4B4B4B"
      mouseLeaveDelay={0}
      styles={{
        body: {
          padding: "16px 20px",
          borderRadius: "4px !important",
        },
      }}
    >
      {isMultiselect ? (
        <CustomFieldChips>
          {selectedOptions.map((option) => (
            <Tag key={option} variant="info">
              {option}
            </Tag>
          ))}
        </CustomFieldChips>
      ) : (
        <CustomFieldValue>{displayValue}</CustomFieldValue>
      )}
    </Tooltip>
  );
}
